import { useEffect, useRef, useState } from 'react';
import api from '../../../services/api.js';

/**
 * Polls a `getUnreadCounts` endpoint for exactly the creative_ids currently in
 * `rows` (i.e. whatever's actually loaded/visible on the page — never a full
 * re-fetch of the list), and returns a `{ [creative_id]: count }` map to
 * overlay onto rows at render time.
 *
 * Deliberately read-only and local: it never touches the caller's own row
 * state/setters, so it cannot reset pagination, filters, or expanded rows —
 * worst case a failed poll just leaves the previous badge value in place.
 * The interval starts fresh on mount and is cleared on unmount, so switching
 * tabs (which unmounts the previous tab's component) stops the old poll and
 * a newly mounted tab starts its own from a clean fetch.
 *
 * Some tab hosts (Shoelace's `<sl-tab-group>`) hide inactive panels with CSS
 * rather than unmounting them, so a component on a hidden tab stays mounted
 * and would otherwise keep polling forever in the background. Pass
 * `enabled: false` while this component's own tab isn't the active one to
 * stop that — the interval is cleared immediately and a fresh one starts
 * (with an immediate fetch) the moment `enabled` flips back to true.
 *
 * `visibleIds` (optional, e.g. from `useVisibleRowIds`) narrows the poll to
 * only rows currently scrolled into view — needed for infinite-scroll lists
 * where `rows` keeps growing as more pages load, so polling "all rows ever
 * loaded" would otherwise send an ever-larger id list forever. Read from a
 * ref (not a poll-effect dependency) so scrolling doesn't reset the interval
 * timer. Ids are deduped before every request regardless.
 */
export const useUnreadCountPolling = (rows, endpoint, { intervalMs = 5000, idField = 'creative_id', enabled = true, visibleIds = null } = {}) => {
  const [liveUnread, setLiveUnread] = useState({});
  const rowsRef = useRef(rows);
  rowsRef.current = rows;
  const visibleIdsRef = useRef(visibleIds);
  visibleIdsRef.current = visibleIds;

  useEffect(() => {
    if (!endpoint || !enabled) return undefined;
    let cancelled = false;

    const poll = async () => {
      let ids = (rowsRef.current || [])
        .map((row) => row?.[idField])
        .filter((id) => id != null);
      const visible = visibleIdsRef.current;
      if (visible) ids = ids.filter((id) => visible.has(id));
      ids = [...new Set(ids)];
      if (!ids.length) return;
      try {
        const res = await api({
          url: endpoint,
          method: 'POST',
          responseType: 'json',
          data: { creative_ids: ids },
        });
        if (cancelled || res.code !== 200) return;
        const next = {};
        ids.forEach((id) => { next[id] = 0; });
        (res.data?.data || []).forEach((r) => {
          next[r.creative_id] = Number(r.unread_count) || 0;
        });
        // Merge, don't replace: when `visibleIds` narrows `ids` to a subset,
        // a replace would drop the last-known count for now-offscreen rows.
        setLiveUnread((prev) => ({ ...prev, ...next }));
      } catch (error) {
        // Silent on purpose — a missed refresh just leaves the previous badge
        // value in place; this background poll must never break the page.
      }
    };

    poll();
    const intervalId = setInterval(poll, intervalMs);
    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
  }, [endpoint, intervalMs, idField, enabled]);

  return liveUnread;
};

export default useUnreadCountPolling;
